import type { UseFormReset } from "react-hook-form";
import { INFRACTION_API_STATUS } from "../constants/infraction-api-enums.constant";
import type { InfractionCreateSchema } from "../schemas/infraction-create.schema";
import type {
	InfractionDetailApiResponse,
	InfractionIssuingAuthorityLookupApiResponse,
	UpdateInfractionRequest,
} from "../types/infraction-api.types";
import {
	buildCatalogSelectItemFromDetail,
	buildIssuingAuthorityLookupFromDetail,
	buildOccurredAtIso,
	formatNumberToDisplayBR,
	type InfractionCatalogSelectItem,
	type InfractionVehicleSelectItem,
	resolveOptionalDateField,
} from "./infraction-create-form.helpers";
import {
	type InfractionAttachmentSlots,
	mapInfractionAttachmentsToSlots,
} from "./map-infraction-attachments-to-slots";
import { mapInfractionDetailToFormValues } from "./map-infraction-detail-to-form";

export const EDIT_STATUS_OVERRIDE = {
	PAID: String(INFRACTION_API_STATUS.PAID),
	CANCELLED: String(INFRACTION_API_STATUS.CANCELLED),
} as const;

export function buildEditVehicleDisplay(
	detail: InfractionDetailApiResponse
): InfractionVehicleSelectItem {
	const brandModel = [detail.vehicleBrand, detail.vehicleModel]
		.filter(Boolean)
		.join(" ");
	const plate = detail.plate ?? "";
	const displayTitle = brandModel ? `${plate} • ${brandModel}` : plate;

	return {
		id: String(detail.vehicleId),
		value: detail.vehicleId,
		plate,
		brand: detail.vehicleBrand ?? "",
		model: detail.vehicleModel ?? "",
		label: displayTitle,
		displayTitle,
	};
}

export function resolveEditStatusOverride(status: number): string | null {
	if (status === INFRACTION_API_STATUS.PAID) {
		return EDIT_STATUS_OVERRIDE.PAID;
	}

	if (status === INFRACTION_API_STATUS.CANCELLED) {
		return EDIT_STATUS_OVERRIDE.CANCELLED;
	}

	return null;
}

type ApplyDetailToEditFormParams = {
	detail: InfractionDetailApiResponse;
	reset: UseFormReset<InfractionCreateSchema>;
	setAmountDisplay: (value: string) => void;
	setStatusOverride: (value: string | null) => void;
	setEditVehicleDisplay: (value: InfractionVehicleSelectItem) => void;
};

export function applyDetailToEditForm({
	detail,
	reset,
	setAmountDisplay,
	setStatusOverride,
	setEditVehicleDisplay,
}: ApplyDetailToEditFormParams) {
	reset(mapInfractionDetailToFormValues(detail));
	setAmountDisplay(formatNumberToDisplayBR(detail.originalAmount));
	setStatusOverride(null);
	setEditVehicleDisplay(buildEditVehicleDisplay(detail));
}

export function hasLocalAttachmentFormChanges(
	values: Pick<
		InfractionCreateSchema,
		"infractionNoticeFile" | "penaltyNotificationFile" | "receiptsFile"
	>,
	removedSlotKeys: ReadonlySet<string>
): boolean {
	if (removedSlotKeys.size > 0) {
		return true;
	}

	if (values.infractionNoticeFile) {
		return true;
	}

	if (values.penaltyNotificationFile) {
		return true;
	}

	return Boolean(values.receiptsFile);
}

type HydrateEditLookupsResult = {
	catalogItem: InfractionCatalogSelectItem | null;
	authorityLookup: InfractionIssuingAuthorityLookupApiResponse | null;
	attachmentSlots: InfractionAttachmentSlots;
	catalogSearch: string;
	authoritySearch: string;
};

export function hydrateEditLookupsFromDetail(
	detail: InfractionDetailApiResponse
): HydrateEditLookupsResult {
	const authorityLookup = buildIssuingAuthorityLookupFromDetail(detail);

	return {
		catalogItem: buildCatalogSelectItemFromDetail(detail),
		authorityLookup,
		attachmentSlots: mapInfractionAttachmentsToSlots(detail.attachments),
		catalogSearch: detail.infractionCode ?? "",
		authoritySearch: authorityLookup?.code ?? "",
	};
}

export type SyncAttachmentsFromDetailResult =
	| { shouldSync: false }
	| { shouldSync: true; attachmentSlots: InfractionAttachmentSlots };

export function resolveAttachmentSlotsSyncFromDetail(
	detail: InfractionDetailApiResponse,
	formValues: Pick<
		InfractionCreateSchema,
		"infractionNoticeFile" | "penaltyNotificationFile" | "receiptsFile"
	>,
	removedSlotKeys: ReadonlySet<string>
): SyncAttachmentsFromDetailResult {
	if (hasLocalAttachmentFormChanges(formValues, removedSlotKeys)) {
		return { shouldSync: false };
	}

	return {
		shouldSync: true,
		attachmentSlots: mapInfractionAttachmentsToSlots(detail.attachments),
	};
}

export function buildUpdateInfractionPayload(
	values: InfractionCreateSchema,
	detail: InfractionDetailApiResponse,
	status?: number
): UpdateInfractionRequest {
	const dueDate = resolveOptionalDateField(values.dueDate);

	return {
		...(status !== undefined ? { status } : {}),
		vehicleId: detail.vehicleId,
		driverId: values.driverId ?? null,
		transferPointsToDriver: values.transferPointsToDriver,
		offenderType: values.offenderType,
		issuingAuthorityId: values.issuingAuthorityId,
		ait: values.ait.trim().toUpperCase(),
		infractionCode: values.infractionCode || null,
		breakdown: values.breakdown || null,
		description: values.description || null,
		occurredAt: buildOccurredAtIso(values.occurredDate, values.occurredTime),
		location: values.location || null,
		severity:
			values.severity !== null &&
			values.severity !== undefined &&
			values.severity > 0
				? values.severity
				: null,
		points: values.points ?? null,
		originalAmount: values.originalAmount,
		driverIndicationDeadline: values.driverIndicationDeadline || null,
		dueDate,
		paymentDate: values.paymentDate || null,
	};
}
